import React, { FC } from 'react'
import { useForm, SubmitErrorHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput'
import { Box } from 'transfix-ui/core'
import * as yup from 'yup'
import { Button } from 'transfix-ui'

const requiredMessage = 'This field is required.'
const phoneMessage = 'Please enter a 10-digit phone number.'

const PhoneSchema = yup.string().nullable().min(10, phoneMessage).max(10, phoneMessage)

export const ReturningUserSchema = yup.object({
  firstName: yup.string().nullable().required(requiredMessage).default(''),
  lastName: yup.string().nullable().required(requiredMessage).default(''),
  phone: PhoneSchema.required(requiredMessage),
  cellPhone: PhoneSchema.notRequired(),
})

export type ReturningUserSchemaType = yup.InferType<typeof ReturningUserSchema>

export interface ISignupFormProps {
  error?: string
  defaultValues?: Partial<ReturningUserSchemaType>
  isLoading?: boolean
  onSuccess: (data: ReturningUserSchemaType) => void
  onError: SubmitErrorHandler<ReturningUserSchemaType>
}

/**
 * Sign up form for FP Onboarding Wizard
 */
export const ReturningUserForm: FC<ISignupFormProps> = ({
  defaultValues = {},
  onSuccess: onSuccessProp,
  onError: onErrorProp,
  isLoading,
}) => {
  const { control, errors: formErrors, handleSubmit } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(ReturningUserSchema),
    shouldFocusError: true,
    defaultValues: {
      ...ReturningUserSchema.cast({
        ...defaultValues,
      }),
    },
  })

  const submitHandler = handleSubmit(onSuccessProp, onErrorProp)

  const isSubmitDisabled = !!Object.keys({ ...formErrors }).length || isLoading

  return (
    <form onSubmit={submitHandler}>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='firstName'
          TextInputProps={{
            dataTest: 'input-firstName',
            clearable: false,
            label: 'First Name',
            placeholder: 'Enter your first name',
            large: true,
            validationMessage: formErrors?.firstName?.message,
            invalid: !!formErrors?.firstName?.message,
            // Autofocus only if this field is blank
            autoFocus: !defaultValues?.firstName,
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='lastName'
          TextInputProps={{
            dataTest: 'input-lastName',
            clearable: false,
            label: 'Last Name',
            placeholder: 'Enter your last name',
            large: true,
            validationMessage: formErrors?.lastName?.message,
            invalid: !!formErrors?.lastName?.message,
          }}
        />
      </Box>
      <Box mb={2}>
        <ControlledPhoneInput
          control={control}
          name='phone'
          PhoneInputProps={{
            dataTest: 'input-phone',
            clearable: false,
            label: 'Phone',
            placeholder: '(555) 123-5678',
            large: true,
            validationMessage:
              formErrors?.phone?.message ||
              'We ask for this in case we need to contact you about your shipments.',
            invalid: !!formErrors?.phone?.message,
            className: 'fs-mask',
          }}
        />
      </Box>
      <Box mb={4}>
        <ControlledPhoneInput
          control={control}
          name='cellPhone'
          PhoneInputProps={{
            dataTest: 'input-cellPhone',
            clearable: false,
            label: 'Cellphone (Optional)',
            placeholder: '(555) 123-5678',
            large: true,
            validationMessage:
              formErrors?.cellPhone?.message ||
              'We ask for this in case we need to contact you about your shipments.',
            invalid: !!formErrors?.cellPhone?.message,
            className: 'fs-mask',
          }}
        />
      </Box>
      <Box>
        <Button
          color='primary'
          onClick={submitHandler}
          type='submit'
          size='large'
          fullWidth
          data-test='continue-button'
          disabled={isSubmitDisabled}>
          Looks Good
        </Button>
      </Box>
    </form>
  )
}
